interface DensityFeature {
  properties?: { value?: number | null } | null;
  // `unknown`, not a typed GeoJSON geometry: callers (DmaChoropleth,
  // CityChoropleth, ZipChoropleth) all type their own feature's `geometry` as
  // `unknown` too, since it's passed straight through from the boundary APIs.
  geometry?: unknown;
}

interface ViewState {
  latitude: number;
  longitude: number;
  zoom: number;
  bearing: number;
  pitch: number;
}

// Matches legacy's fixed camera zoom on focus (components/Map/helpers.js
// fitBounds) — close enough to read the densest area's shape without
// over-zooming past its boundary. Loosened from legacy's original 8 (live
// QA + product feedback, #0000): zoom 8 filled the viewport with just the
// top city/ZIP's shape and no surrounding context.
const FOCUSED_ZOOM = 6;

// Market (DMA) polygons span whole metro regions, not a single city/ZIP —
// reusing FOCUSED_ZOOM filled the viewport with one solid market polygon and
// no boundaries visible (flank#4748 only specs auto-zoom-to-data for
// City/ZIP). Market still centers on its densest feature, just notably
// wider than the City/ZIP framing above.
export const MARKET_FOCUSED_ZOOM = 4;

const extractLngLatPairs = (geometry: unknown): [number, number][] => {
  const geo = geometry as { type?: string; coordinates?: unknown } | null | undefined;
  if (!geo?.coordinates) return [];
  const { type, coordinates } = geo;
  if (type === 'Point') return [coordinates as [number, number]];
  if (type === 'Polygon') return (coordinates as [number, number][][])[0] ?? [];
  if (type === 'MultiPolygon') return (coordinates as [number, number][][][]).flatMap(polygon => polygon[0] ?? []);
  return [];
};

/** Bounding-box center of one feature's geometry, at the given zoom — the
 * shared math behind both "zoom to the densest feature" and "zoom to the
 * one the user clicked." Returns null when the geometry has no usable
 * points (callers fall back to their own default view). */
const boundingBoxViewState = (geometry: unknown, zoom: number): ViewState | null => {
  const points = extractLngLatPairs(geometry);
  if (!points.length) return null;

  let north = -90;
  let south = 90;
  let east = -180;
  let west = 180;
  for (const [lon, lat] of points) {
    north = Math.max(north, lat);
    south = Math.min(south, lat);
    east = Math.max(east, lon);
    west = Math.min(west, lon);
  }

  return {
    latitude: (north + south) / 2,
    longitude: (east + west) / 2,
    zoom,
    bearing: 0,
    pitch: 0,
  };
};

/**
 * #0000 — "follow legacy": zoom the map's initial viewport to the single
 * densest feature (highest `value`) instead of a fixed default view, the
 * same behaviour legacy's fitCityBounds/fitBounds (components/Map/helpers.js)
 * produce — sort by impressions, take the top feature, center on its
 * bounding box. Falls back to `fallback` (the existing default view) when
 * there's no positive-value feature to focus on (e.g. boundaries still
 * loading, or every value is null/0).
 */
export const computeDensityFocusedViewState = <F extends DensityFeature>(
  features: F[],
  fallback: ViewState,
  zoom: number = FOCUSED_ZOOM,
): ViewState => {
  let densest: F | null = null;
  let max = -Infinity;
  for (const feature of features) {
    const value = Number(feature.properties?.value);
    if (Number.isFinite(value) && value > 0 && value > max) {
      max = value;
      densest = feature;
    }
  }
  if (!densest) return fallback;
  return boundingBoxViewState(densest.geometry, zoom) ?? fallback;
};

/**
 * flank#4755 — "clicking a City or ZIP row zooms the map to it": the same
 * bounding-box framing as the density auto-focus above, but centered on
 * whichever feature `matchKey(feature) === key` instead of the densest one.
 * `key` is null when nothing's been clicked (or the click was cleared), in
 * which case this is a no-op — callers keep whatever viewState they already
 * have rather than snapping back to a default.
 */
export const computeFeatureFocusViewState = <F extends DensityFeature>(
  features: F[],
  key: string | null,
  matchKey: (feature: F) => string,
  zoom: number = FOCUSED_ZOOM,
): ViewState | null => {
  if (!key) return null;
  const match = features.find(feature => matchKey(feature) === key);
  if (!match) return null;
  return boundingBoxViewState(match.geometry, zoom);
};

/** flank#4755 — a click-to-zoom request: `requestId` changes on every click,
 * even a repeat click on the same `key`, so {@link computeClickFocusViewState}
 * can tell "click this row again" apart from "the dataset changed under an
 * already-applied selection" (CodeRabbit review on #4777) even though both
 * produce the same `key` string twice in a row. */
export interface FocusRequest {
  key: string;
  requestId: number;
}

/** Remembers the last `features` array {@link computeAutoFocusViewState} auto-focused for. */
export interface FocusTracker<F> {
  lastFocused: F[] | null;
}

/** Remembers the last {@link FocusRequest.requestId} {@link computeClickFocusViewState} successfully applied. */
export interface ClickFocusTracker {
  lastAppliedRequestId: number | null;
}

export const createClickFocusTracker = (): ClickFocusTracker => ({ lastAppliedRequestId: null });

export const createFocusTracker = <F>(): FocusTracker<F> => ({ lastFocused: null });

/**
 * flank#4748 — City/ZIP must re-centre when a filter change brings in new
 * data, not just on initial mount, while still leaving a user's own pan/zoom
 * alone. Callers memoize `features` on their actual data inputs (rows +
 * boundaries) so its reference only changes when a new filtered dataset
 * lands — an unrelated re-render (panning, hovering) passes the SAME
 * `features` reference back in. This fires once per distinct `features`
 * reference and returns null otherwise, so callers know to leave the current
 * viewState untouched rather than resetting it on every render.
 */
export const computeAutoFocusViewState = <F extends DensityFeature>(
  tracker: FocusTracker<F>,
  features: F[],
  fallback: ViewState,
): ViewState | null => {
  if (tracker.lastFocused === features) return null;
  tracker.lastFocused = features;
  if (features.length === 0) return null;
  return computeDensityFocusedViewState(features, fallback);
};

/**
 * flank#4755/#4777 — like {@link computeFeatureFocusViewState}, but tracks
 * whether `request` has already been successfully applied once. A dataset
 * change (new `features`) re-runs the click-focus effect even though
 * `request` itself didn't change; without this, a stale, already-applied
 * selection would keep re-matching against the new dataset and override its
 * auto-focus (CodeRabbit). A request that hasn't matched yet (boundaries
 * still loading) keeps retrying on every `features` change until it does.
 */
export const computeClickFocusViewState = <F extends DensityFeature>(
  tracker: ClickFocusTracker,
  features: F[],
  request: FocusRequest | null,
  matchKey: (feature: F) => string,
  zoom: number = FOCUSED_ZOOM,
): ViewState | null => {
  if (!request || tracker.lastAppliedRequestId === request.requestId) return null;
  const viewState = computeFeatureFocusViewState(features, request.key, matchKey, zoom);
  if (!viewState) return null;
  tracker.lastAppliedRequestId = request.requestId;
  return viewState;
};
